import type {
  CartItem as PodCartItem,
  CartItemUpdate,
  PaymentPayerType,
} from '@mathflat/domain/PodCart'
import type { PreOrderItem } from '@mathflat/domain/PodOrder'
import { cloneDeep } from 'lodash-es'
import { action, computed, makeObservable, observable } from 'mobx'

import { DTO } from '@/dto'

import { CartItemStudent } from './CartItemStudent'

type KeysOfUnion<T> = T extends T ? keyof T : never
type ValueOfUnion<T, K extends PropertyKey> = T extends Record<K, infer V> ? V : never
type DistributiveOmit<T, K extends PropertyKey> = T extends T ? Omit<T, K> : never

// 여러 종류의 항목을 한 화면 모델에 담는다. 모든 종류에 있는 칸은 그대로, 일부 종류에만 있는 칸은 선택 필드가 된다
type Flatten<T> = { [K in Extract<keyof T, PropertyKey>]: T[K] } & {
  [K in Exclude<KeysOfUnion<T>, keyof T>]?: ValueOfUnion<T, K>
}

// 주문서 항목도 장바구니에서 넘어온 항목이라 같은 화면 모델로 그린다. 주문서 항목은 결제 주체·최소 수량·
// 할인 전 가격 등을 싣지 않고 cartItemId 로 장바구니 항목을 가리킨다
type CartItemSource =
  | PodCartItem
  | (DistributiveOmit<PreOrderItem, 'cartItemId'> & { id: PodCartItem['id'] })

type CartItemProps = Flatten<CartItemSource>

// 수정 요청에서 상품 종류마다 달리 싣는 값. 담을 때 정해지고 화면에서 바뀌지 않는다
type UpdateOption =
  | { productType: 'PRINT_DOCUMENT' }
  | Pick<
      Exclude<PodCartItem, { productType: 'PRINT_DOCUMENT' }>,
      'productType' | 'pdfType' | 'showPrice'
    >

export class CartItem extends DTO {
  id: CartItemProps['id']
  productType: CartItemProps['productType']
  customSignatureWorkbookId?: CartItemProps['customSignatureWorkbookId']
  customWorkbookId?: CartItemProps['customWorkbookId']
  printDocumentId?: CartItemProps['printDocumentId']
  minQuantity?: CartItemProps['minQuantity']
  signatureType?: CartItemProps['signatureType']
  paymentPayerType?: PaymentPayerType
  pdfType?: CartItemProps['pdfType']
  printType: CartItemProps['printType']
  showPrice?: CartItemProps['showPrice']
  autoScored?: CartItemProps['autoScored']
  name: CartItemProps['name']
  pairFlag?: CartItemProps['pairFlag']
  pageCount: CartItemProps['pageCount']
  problemPageCount?: CartItemProps['problemPageCount']
  problemAnswerPageCount?: CartItemProps['problemAnswerPageCount']
  price: CartItemProps['price']
  additionalPrice?: CartItemProps['additionalPrice']
  thumbnailImageUrl: CartItemProps['thumbnailImageUrl']
  productId: CartItemProps['productId']
  coverData: CartItemProps['coverData'] = null
  innerPdfUrl?: CartItemProps['innerPdfUrl']
  discountAmount: CartItemProps['discountAmount']
  productDiscountRate?: CartItemProps['productDiscountRate']
  discountedPrice?: CartItemProps['discountedPrice']
  productAmount: CartItemProps['productAmount']
  grade?: CartItemProps['grade']
  semester?: CartItemProps['semester']
  revision?: CartItemProps['revision']
  schoolType?: CartItemProps['schoolType']
  workbookParts?: CartItemProps['workbookParts']

  parentOrders: CartItemProps['parentOrders'] = null
  students: CartItemStudent[] = []

  private readonly updateOption: UpdateOption

  static fromCartItem(item: PodCartItem) {
    return new CartItem(item)
  }

  static fromPreOrderItem({ cartItemId, ...item }: PreOrderItem) {
    return new CartItem({ ...item, id: cartItemId })
  }

  get studentNames() {
    return this.students.map((student) => student.name)
  }

  get filledStudentNames() {
    return this.students.filter((student) => student.name !== '')
  }

  get quantity() {
    return this.students.length
  }

  constructor(source: CartItemSource) {
    super()
    const props: CartItemProps = source
    this.updateOption =
      source.productType === 'PRINT_DOCUMENT'
        ? { productType: source.productType }
        : { productType: source.productType, pdfType: source.pdfType, showPrice: source.showPrice }

    makeObservable(this, {
      id: observable,
      customSignatureWorkbookId: observable,
      customWorkbookId: observable,
      signatureType: observable,
      productType: observable,
      pdfType: observable,
      printType: observable,
      showPrice: observable,
      autoScored: observable,
      name: observable,
      pairFlag: observable,
      pageCount: observable,
      problemPageCount: observable,
      problemAnswerPageCount: observable,
      price: observable,
      additionalPrice: observable,
      thumbnailImageUrl: observable,
      minQuantity: observable,
      productId: observable,
      coverData: observable,
      innerPdfUrl: observable,
      discountAmount: observable,
      productDiscountRate: observable,
      discountedPrice: observable,
      productAmount: observable,

      grade: observable,
      semester: observable,
      revision: observable,
      schoolType: observable,

      totalPrice: computed,
      getUpdateParams: action,

      students: observable,
      parentOrders: observable,
      studentNames: computed,
      quantity: computed,
    })

    this.id = props.id
    this.customSignatureWorkbookId = props.customSignatureWorkbookId
    this.customWorkbookId = props.customWorkbookId
    this.printDocumentId = props.printDocumentId
    this.minQuantity = props.minQuantity
    this.productType = props.productType
    this.paymentPayerType = props.paymentPayerType
    this.pdfType = props.pdfType
    this.printType = props.printType
    this.signatureType = props.signatureType
    this.showPrice = props.showPrice
    this.autoScored = props.autoScored
    this.name = props.name
    this.pairFlag = props.pairFlag
    this.pageCount = props.pageCount
    this.problemPageCount = props.problemPageCount
    this.problemAnswerPageCount = props.problemAnswerPageCount
    this.price = props.price
    this.additionalPrice = props.additionalPrice
    this.productDiscountRate = props.productDiscountRate
    this.discountedPrice = props.discountedPrice
    this.productAmount = props.productAmount
    this.thumbnailImageUrl = props.thumbnailImageUrl
    this.productId = props.productId
    if (props.coverData) {
      this.coverData = cloneDeep(props.coverData)
    }
    this.innerPdfUrl = props.innerPdfUrl
    this.discountAmount = props.discountAmount
    this.workbookParts = props.workbookParts

    this.grade = props.grade
    this.semester = props.semester
    this.revision = props.revision
    this.schoolType = props.schoolType

    this.parentOrders = props.parentOrders ?? null
    this.students =
      props.paymentPayerType === 'PARENT_PAYMENT' && props.parentOrders
        ? props.parentOrders.map(
            (parentOrder) =>
              new CartItemStudent({
                studentId: parentOrder.studentId,
                name: parentOrder.studentName,
                printingUserName: parentOrder.printingUserName,
                parentPhoneNumber: parentOrder.parentPhoneNumber,
              }),
          )
        : props.studentNames.map((studentName) => new CartItemStudent({ name: studentName }))
  }

  get priceWithAdditionalPrice() {
    return this.price + (this.additionalPrice ?? 0)
  }

  get totalPrice() {
    return this.productAmount - this.discountAmount
  }

  getUpdateParams(paymentPayerType: PaymentPayerType): CartItemUpdate {
    const common = {
      productId: this.productId,
      additionalPrice: this.additionalPrice,
      quantity: this.quantity,
      printType: this.printType,
      studentNames: this.studentNames,
    }
    if (this.updateOption.productType === 'PRINT_DOCUMENT') {
      return { ...common, ...this.updateOption }
    }
    return {
      ...common,
      ...this.updateOption,
      parentOrders:
        paymentPayerType === 'PARENT_PAYMENT'
          ? this.students.map((student) => student.toParentOrders())
          : null,
    }
  }
}
